'use client';
import { RefObject, Suspense, useCallback, useEffect, useMemo, useRef, useState, lazy } from "react";
import { useShallow } from 'zustand/react/shallow';
import { MessageCircleMore, Phone } from "lucide-react";

import useAudioStore from "@/store/useAudioStore";
import { useChatStore } from "@/store/useChatStore";
import { useMessageStore } from "@/store/useMessageStore";
import useAuthStore from "@/store/useAuthStore";

import Header from "@/components/Header";
import Volume from "@/components/Volume";
import LearnCard from "@/components/LearnCard";
import LearnInput from "@/components/LearnCard/learn-input";
import CardInfo from "@/components/LearnCard/card-info";
import LiveTutorCard from "@/components/LearnCard/live-tutor-card";
import MarkdownComponent from "@/components/Markdown";
import { useFinishTask } from "@/components/Confetti/FinishTask";
import { AssistantAvatar, LetterAvatar } from "@/components/Avatar";
import {
  PublicStudyAuthPromptModal,
  PublicStudyAuthPromptProvider,
  type PublicStudyAuthPromptReason,
} from "@/components/public-study/PublicStudyAuthPrompt";

import { resetDemoReward } from "@/constant/reward";
import { colors } from "@/constant/colors";
import { ElementType, ModeType, Quest } from "@/constant/schema";
import STORAGE_KEYS from "@/constant/storage";
import { finishLesson } from "@/constant/tasks";

import { useDeviceType } from "@/lib/hooks";
import useLLMTeacherStore from '@/store/useLLMTeacherStore';
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import UserTrackService from "@/lib/UserTrackService";
import { getDisabledContinueButtonTips, isLearnTest } from "@/lib/utils";
import UserQuestProgressService from "@/lib/userQuestProgressService";
import { getQuestionById } from "@/lib/practiceHelper";
import useConfigStore from "@/store/useConfigStore";
import useRewardStore from "@/store/useRewardStore";
import { useOverlay } from "@/components/Provider/OverlayProvider";
import { HideMoreDetailPosition, InteractiveElementTypes, QuizElementTypes } from "@/constant/element-type-group";
import NormalButton from "@/components/Button/normal";
import LiveButton from "@/components/Button/LiveButton";
import LiveExplainButton from "@/components/LearnCard/live-explain-button";
import {
  buildKeyPropertyLiveTutorSelection,
  buildQuestionCardLiveTutorProblem,
  isMathLearnLiveTutorEnabled,
} from "@/components/LearnCard/live-tutor-selection";
import TypewriterEffect from "@/components/TypewriterEffect";
import { useWindowSize } from "@/constant/hooks";
import { useStreakStore } from '@/store/useStreakStore';
import report, { ReportEvent } from "@/lib/report";
import Image from "@/components/Image";
import { createFeedActivity } from '@/lib/feedActivityService';
import { isParentProfile, isStudentProfile } from "@/lib/profile-role";
import supabase from '@/lib/supabase';
import type { ParentSkillPreviewCard } from "@/types/parent-search";
import type { DemoSelection } from "@/components/whiteboard-v2/LiveWhiteboard";
import type { LiveModeSessionOptions, ProblemContext } from "@/components/tutor/live-mode";
import type { BoardEntry, StoredProblem } from "@/components/whiteboard-v2/types";
import { checkSubjectAccess } from '@/hooks/useSubjectGate';
import SubjectManagementModal from '@/components/SubjectManagementModal';
import ConfirmSubjectSelectionModal from '@/components/ConfirmSubjectSelectionModal';

// Lazy load components that are controlled by state
const CongratPopup = lazy(() => import("@/components/Popup/CongratPopup"));
const ReviewModal = lazy(() => import("@/components/Popup/ReviewModal"));
const CustomFeedbackSurvey = lazy(() => import('@/components/CustomFeedbackSurvey'));
const LearnGuideOverlay = lazy(() => import("@/components/GuideOverlay/learn-guide"));
const LearnChildMobileGuide = lazy(() => import("@/components/GuideOverlay/learn-child-mobile-guide"));

const LEARN_LIVE_TUTOR_STORAGE_PREFIX = 'learn-live-tutor-cards';

interface LearnClientProps {
  initialQuest: Quest | null;
  publicQuestId?: string | null;
  publicChapterId?: string | null;
  publicLessonId?: string | null;
  publicGuestMode?: boolean;
  publicSkillId?: string | null;
  publicSkillTitle?: string | null;
  publicSkillCards?: ParentSkillPreviewCard[] | null;
  publicSkillSummary?: string | null;
}

export default function LearnClient({
  initialQuest,
  publicQuestId = null,
  publicChapterId = null,
  publicLessonId = null,
  publicGuestMode = false,
  publicSkillId = null,
  publicSkillTitle = null,
  publicSkillCards = null,
  publicSkillSummary = null,
}: LearnClientProps) {
  type LiveTutorCardItem = {
    id: string;
    sourceElementId: string;
    askPengiCallLessonId?: string;
    problem?: StoredProblem;
    selection?: DemoSelection;
    liveSessionOptions?: Pick<LiveModeSessionOptions, 'buildSystemPrompt' | 'initialMessage'>;
    contextOverrides?: Partial<ProblemContext>;
    startSessionSignal?: number;
    previewEntries?: BoardEntry[];
    previewShowBoardHeader?: boolean;
    previewStarStates?: Array<'pending' | 'success' | 'fail'>;
    createdAt: number;
  };
  type QuizFollowupState = {
    quickExplainUsed?: boolean;
    liveUsed?: boolean;
  };

  // Use LLM Teacher store - 使用选择器模式,只订阅需要的状态
  const llmLoading = useLLMTeacherStore(state => state.isLoading);

  const device = useDeviceType();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const quest_id = publicQuestId || searchParams.get('quest_id') as string;
  const chapter_id = publicChapterId || searchParams.get('chapter_id') as string;
  const lesson_id = publicLessonId || searchParams.get('lesson_id') as string;
  const learningUnitId = publicSkillId || lesson_id;
  const fromURLProgress = searchParams.get('progress') as string;
  const from = searchParams.get('from') || 'quest';
  const forceGuestPreview = searchParams.get('preview') === '1';

  const messagesRef = useRef<HTMLDivElement>(null);
  const [showInput, setShowInput] = useState(false);
  const [showAllChapters, setShowAllChapters] = useState(false);

  // Auth store - 使用 useShallow 合并订阅
  const { user, profile, authInitialized } = useAuthStore(
    useShallow(state => ({
      user: state.user,
      profile: state.profile,
      authInitialized: state.initialized,
    }))
  );
  const isGuestMode = publicGuestMode && authInitialized && !user;
  const isParentPreviewMode = Boolean(user && publicGuestMode && forceGuestPreview && isParentProfile(profile));
  const isStudentMode = isStudentProfile(profile);
  const isPublicSkillLearn = Boolean(publicSkillId && publicSkillCards?.length);
  const isPublicStudyExperience = Boolean(publicGuestMode || publicLessonId || publicSkillId);
  const setDemoRewardMode = useRewardStore(state => state.setDemoRewardMode);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [authPromptReason, setAuthPromptReason] = useState<PublicStudyAuthPromptReason>('ask-pengi');

  const totalDiamondImageRef = useRef<any>(null);
  const [congratPopup, setCongratPopup] = useState(false);
  const hasShownCongratPopupRef = useRef<boolean>(false); // 追踪是否已显示过 congrat popup
  const hasInitializedFallbackMessagesRef = useRef<string | null>(null);
  const initializedSkillContextRef = useRef<string | null>(null);
  const chipHideRef = useRef(false); // 改用 useRef，因为不需要触发渲染
  const [stepRefs, setStepRefs] = useState<(React.RefObject<HTMLDivElement> | null)[]>(new Array(2).fill(null));
  const headerRef = useRef<HTMLDivElement>(null);
  const pengiButtonRef = useRef<HTMLDivElement>(null);
  const askPengiMenuRef = useRef<HTMLDivElement>(null);
  const askPengiMenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showTaskToast } = useFinishTask();
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadMore, setLoadMore] = useState(false);
  const [isAskPengiMenuOpen, setIsAskPengiMenuOpen] = useState(false);
  const scrollyRef = useRef<number>(0);
  const { width: windowWidth } = useWindowSize();
  const chatMarkRef = useRef<boolean>(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const trackingIntervalRef = useRef<NodeJS.Timeout | null>(null); // 改用 useRef
  const [subjectPaywallSubject, setSubjectPaywallSubject] = useState<string | null>(null);
  const [confirmSubjectBeforeStart, setConfirmSubjectBeforeStart] = useState<string | null>(null);

  // 使用 ref 存储函数引用，避免 handleElementFinish 依赖项变化
  const showTaskToastRef = useRef(showTaskToast);
  const fireConfettiRef = useRef<(() => void) | null>(null);

  // Chat store - 使用 useShallow 合并所有相关状态，避免多次渲染
  const {
    quest,
    loading,
    current_element,
    current_lesson,
    can_continue,
    current_chapter,
    mode,
    lesson_finished,
    reviewMode
  } = useChatStore(
    useShallow(state => ({
      quest: state.quest,
      loading: state.loading,
      current_element: state.current_element,
      current_lesson: state.current_lesson,
      can_continue: state.can_continue,
      current_chapter: state.current_chapter,
      mode: state.mode,
      lesson_finished: state.lesson_finished,
      reviewMode: state.reviewMode,
    }))
  );

  // Message store - 单个状态不需要 shallow
  const messages = useMessageStore(state => state.messages);

  // Config store - 使用 useShallow 合并订阅
  const {
    learnTutorial,
    finishLearnTutorial,
    betterExperienceTip,
    checkShowBetterExperienceTip
  } = useConfigStore(
    useShallow(state => ({
      learnTutorial: state.learnTutorial,
      finishLearnTutorial: state.finishLearnTutorial,
      betterExperienceTip: state.betterExperienceTip,
      checkShowBetterExperienceTip: state.checkShowBetterExperienceTip,
    }))
  );

  // Audio store - 使用 useShallow 合并订阅
  const { playingId, isPlaying, playingType } = useAudioStore(
    useShallow(state => ({
      playingId: state.playingId,
      isPlaying: state.isPlaying,
      playingType: state.playingType,
    }))
  );

  const { fireConfetti } = useOverlay();
  const [showIncompleteTip, setShowIncompleteTip] = useState(false);
  const [highlightLastElement, setHighlightLastElement] = useState(false);
  const [liveTutorCards, setLiveTutorCards] = useState<LiveTutorCardItem[]>([]);
  const [quizFollowupStateByElement, setQuizFollowupStateByElement] = useState<Record<string, QuizFollowupState>>({});
  const lastElementRef = useRef<HTMLDivElement>(null);

  // 在每次渲染时更新 ref，确保始终使用最新的函数
  useEffect(() => {
    showTaskToastRef.current = showTaskToast;
    fireConfettiRef.current = fireConfetti;
  });

  const deviceType = useDeviceType();
  const baseHeight = deviceType === 'tablet' ? 60 : 100;

  const requestAuthPrompt = useCallback((reason: PublicStudyAuthPromptReason) => {
    if (!authInitialized || !isGuestMode) {
      return false;
    }
    setAuthPromptReason(reason);
    setShowAuthPrompt(true);
    return true;
  }, [authInitialized, isGuestMode]);

  useEffect(() => {
    if (isParentPreviewMode && !isStudentMode) {
      resetDemoReward();
    }
    setDemoRewardMode(isParentPreviewMode && !isStudentMode);
    return () => {
      setDemoRewardMode(false);
    };
  }, [isParentPreviewMode, isStudentMode, setDemoRewardMode]);

  const buildPublicSkillQuest = useCallback((): Quest | null => {
    if (!isPublicSkillLearn || !publicSkillId || !publicSkillTitle || !publicSkillCards || !quest_id || !chapter_id) {
      return null;
    }

    const elements = publicSkillCards
      .map((card, index) => {
        const renderElement = card.renderElement;
        if (!renderElement || typeof renderElement !== 'object') return null;
        const raw = renderElement as Record<string, unknown>;
        if (typeof raw.element_type !== 'string') return null;
        return {
          ...(raw as Record<string, unknown>),
          id: typeof raw.id === 'string' && raw.id ? raw.id : `${publicSkillId}-${index + 1}`,
          title: typeof raw.title === 'string' && raw.title ? raw.title : card.title,
          lesson_id: publicSkillId,
          order: typeof raw.order === 'number' ? raw.order : index + 1,
          created_at: typeof raw.created_at === 'string' ? raw.created_at : new Date(0).toISOString(),
        };
      })
      .filter(Boolean);

    if (elements.length === 0) {
      return null;
    }

    const syntheticLesson = {
      id: publicSkillId,
      created_at: new Date(0).toISOString(),
      title: publicSkillTitle,
      keypoints: [],
      description: publicSkillSummary || `Learn ${publicSkillTitle} on Pengi.`,
      order: 1,
      chapter_id,
      welcome_message: `Let's learn ${publicSkillTitle}.`,
      lesson_prefix: null,
      questions: [],
      challenge: [],
      textbook_exercises: [],
      play_elements: [],
      type: 'dynamic' as const,
      elements: elements as any,
      skill_list: [publicSkillId],
    };

    return {
      id: quest_id,
      created_at: new Date(0).toISOString(),
      title: publicSkillTitle,
      book_name: publicSkillTitle,
      primary_color_theme: '#42C52D',
      secondary_color_theme: '#EAF8E7',
      description: publicSkillSummary || `Learn ${publicSkillTitle} on Pengi.`,
      instructor_profile: {
        name: 'Pengi',
        avatar_url: '/assets/landing-logo.png',
        full_body_url: null,
        greeting: null,
      },
      cover_image: null,
      lock: false,
      subject: null,
      series: null,
      order: 1,
      visible: true,
      tab_config: {
        showLearn: true,
        showPractice: true,
        showChallenge: true,
        showPlay: false,
        showChapterTest: false,
        showProblemSet: false,
      },
      publisher: null,
      default_grade: null,
      public_school_districts: null,
      school_district_grade_mapping: null,
      chapters: [{
        id: chapter_id,
        created_at: new Date(0).toISOString(),
        title: '',
        description: '',
        order: 1,
        quest_id,
        visible: true,
        unit: '',
        questions: [],
        lesson_list: [syntheticLesson],
      }],
    } as Quest;
  }, [chapter_id, isPublicSkillLearn, publicSkillCards, publicSkillId, publicSkillSummary, publicSkillTitle, quest_id]);

  useEffect(() => {
    if (profile) {
      // 优先检查 localStorage，如果用户已完成 tutorial，则不再显示
      const tutorialFinished = localStorage.getItem(STORAGE_KEYS.LEARN_TUTORIAL);
      if (tutorialFinished === 'false') {
        useConfigStore.setState({ learnTutorial: false });
      } else {
        // 只有 localStorage 中没有标记时，才从 profile 中读取
        useConfigStore.setState({ learnTutorial: profile.learn_tutorial || false });
      }
      checkShowBetterExperienceTip();
    }
  }, [profile]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      // Exit review mode when page unloads
      if (useChatStore.getState().reviewMode) {
        useChatStore.setState({ reviewMode: false });
      }
      useAudioStore.getState().unload();
      useChatStore.getState().uninstall();
    };

    const handleScroll = () => {
      if (window.scrollY > 0) {
        scrollyRef.current = window.scrollY;
      }
    }

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        // 进入全屏时记录滚动位置
      } else {
        // 退出全屏时恢复滚动位置
        window.scrollTo(0, scrollyRef.current);
      }
    };

    // 监听全屏切换事件
    // 解决全屏后，页面滚动到顶部的问题，通过记录最后一次滚动的位置，在关闭全屏之后重新滚动到该位置
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // 添加页面关闭事件监听
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('scroll', handleScroll);

    return () => {
      // 移除事件监听
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // 执行清理 (包括退出 review mode)
      handleBeforeUnload();
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!quest_id || !chapter_id || (!lesson_id && !publicSkillId)) {
      router.push('/quest');
      return;
    }

    if (isPublicStudyExperience && !authInitialized) {
      return;
    }

    // Exit review mode when URL parameters change (user navigated to different lesson)
    if (useChatStore.getState().reviewMode) {
      console.log('🔄 URL changed, exiting review mode');
      useChatStore.setState({ reviewMode: false });
    }

    // Reset congrat popup flag when navigating to different lesson
    hasShownCongratPopupRef.current = false;

    const initializeChat = async () => {
      try {
        if (isPublicSkillLearn) {
          const skillContextKey = `${quest_id}:${chapter_id}:${publicSkillId}`;
          if (initializedSkillContextRef.current === skillContextKey) {
            return;
          }
          initializedSkillContextRef.current = skillContextKey;

          const syntheticQuest = buildPublicSkillQuest();
          if (!syntheticQuest) {
            initializedSkillContextRef.current = null;
            router.push('/search');
            return;
          }

          const chapter = syntheticQuest.chapters[0];
          const syntheticLesson = chapter.lesson_list[0];
          const elementMap = syntheticLesson.elements.reduce((acc: Record<string, any>, element: any) => {
            acc[element.id] = {
              ...element,
              finished: false,
            };
            return acc;
          }, {});

          useChatStore.setState({
            loading: false,
            questId: quest_id,
            chapterId: chapter_id,
            quest: syntheticQuest,
            mode: ModeType.LEARN,
            current_chapter: chapter as any,
            current_lesson: syntheticLesson as any,
            current_element: syntheticLesson.elements[0] as any,
            element_map: elementMap,
            can_continue: true,
            lesson_finished: false,
            reviewMode: false,
            element_display_states: {},
          });

          hasInitializedFallbackMessagesRef.current = `${quest_id}:${chapter_id}:${publicSkillId}`;
          await useMessageStore.getState().clearAndReinitialize(
            quest_id,
            chapter_id,
            publicSkillId!,
            syntheticQuest,
            ModeType.LEARN
          );

          useLLMTeacherStore.getState().initialize(syntheticQuest, chapter_id, publicSkillId!);
          return;
        }

        // Use initialQuest if available, otherwise fetch
        if (initialQuest) {
          // Pre-populate the chat store with server data for faster initial load
          useChatStore.setState({ quest: initialQuest });
        }

        await useChatStore.getState().initQuest(quest_id, chapter_id, lesson_id, ModeType.LEARN);
        // Initialize LLM Teacher when quest is loaded
        const questData = useChatStore.getState().quest;

        // Free-tier subject gate — skip only for true unauthenticated guests
        if (questData?.subject && !isGuestMode) {
          const access = await checkSubjectAccess(questData.subject);
          if (access === 'needs_selection') {
            setConfirmSubjectBeforeStart(questData.subject);
            return;
          }
          if (access === 'blocked') {
            setSubjectPaywallSubject(questData.subject);
            return;
          }
        }

        if (questData) {
          useLLMTeacherStore.getState().initialize(questData, chapter_id, lesson_id);

          // Load historical messages into LLM store
          const historicalMessages = useMessageStore.getState().messages;
          if (historicalMessages && historicalMessages.length > 0) {
            useLLMTeacherStore.getState().loadFromHistory(historicalMessages);
          } else {
            // 如果没有历史记录，添加欢迎消息和第一个元素
            const chapter = questData.chapters?.find(ch => ch.id === chapter_id);
            const lesson = chapter?.lesson_list?.find(l => l.id === lesson_id);
            if (lesson?.welcome_message) {
              useLLMTeacherStore.getState().addAssistantMessage(lesson.welcome_message);
            }
            if (lesson?.elements && lesson.elements.length > 0) {
              useLLMTeacherStore.getState().addElementMessage(lesson.elements[0]);
            }
          }
        }
      } catch (error) {
        if (isPublicSkillLearn) {
          initializedSkillContextRef.current = null;
        }
        console.error("Error initializing chat:", error);
        router.push('/quest');
      }
    };

    // initializedRef = true;
    if (chapter_id.length > 0 && quest_id.length > 0) {
      initializeChat();
    }
  }, [authInitialized, buildPublicSkillQuest, chapter_id, initialQuest, isPublicSkillLearn, isPublicStudyExperience, lesson_id, publicSkillId, quest_id, router]);

  useEffect(() => {
    if (isPublicSkillLearn) {
      return;
    }

    if (loading || !quest || !current_lesson) {
      return;
    }

    if (messages.length > 0) {
      hasInitializedFallbackMessagesRef.current = `${quest_id}:${chapter_id}:${learningUnitId}`;
      return;
    }

    const contextKey = `${quest_id}:${chapter_id}:${learningUnitId}`;
    if (hasInitializedFallbackMessagesRef.current === contextKey) {
      return;
    }

    hasInitializedFallbackMessagesRef.current = contextKey;

    void useMessageStore.getState().clearAndReinitialize(
      quest_id,
      chapter_id,
      learningUnitId,
      quest,
      ModeType.LEARN
    );
  }, [isPublicSkillLearn, loading, quest, current_lesson, messages.length, quest_id, chapter_id, learningUnitId]);

  const currentProgress = useMemo(() => {
    if (!current_lesson) {
      return 0;
    }
    const total_elements = current_lesson.elements.length;
    const current_element_index = current_lesson.elements.findIndex(element => element.id === current_element?.id);
    const progress = (current_element_index + 1) / total_elements;

    return progress;
  }, [current_element, current_lesson]);

  // 使用 useMemo 派生 canReview 状态，避免 useState 导致的额外渲染
  const canReview = useMemo(() => {
    return currentProgress === 1 && fromURLProgress === '100';
  }, [currentProgress, fromURLProgress]);

  const handleElementFinish = useCallback(async (
    element_id: string,
    finish_type: 'user_proceed' | 'user_close' | 'auto_finish'
  ) => {
    useAudioStore.getState().interruptAudio();
    // Only update progress in non-review mode
    const reviewMode = useChatStore.getState().reviewMode;
    const current_lesson = useChatStore.getState().current_lesson;

    const last_element = useChatStore.getState().current_element;
    if (last_element?.id !== element_id || !current_lesson) {
      console.log("last_element?.id !== element_id", last_element?.id, element_id);
      return;
    }

    setIsGenerating(true);

    const has_next = await useChatStore.getState().next_element();
    console.log('🐘 has_next', has_next);

    if (has_next) {
      // 这里必须显式的重新获取最新值，因为next_element修改了current_element
      // 直接使用current_element会获取到旧值
      const current_element = useChatStore.getState().current_element;
      const mode = useChatStore.getState().mode;
      const user = useAuthStore.getState().user;

      useMessageStore.getState().addMessage({
        role: 'assistant',
        type: 'element',
        element: current_element || undefined,
      });

      const total_elements = current_lesson.elements.length;
      const current_element_index = current_lesson.elements.findIndex(element => element.id === current_element?.id);
      const progress = (current_element_index + 1) / total_elements;

      if (user && current_element && !reviewMode && !isPublicSkillLearn) {
        UserQuestProgressService.upsertProgress({
          user_id: user.id,
          quest_id: quest_id,
          chapter_id: chapter_id,
          lesson_id: lesson_id || 'empty',
          element_id: current_element.id,
          mode_type: mode,
          progress: progress * 100,
        }).catch(error => {
          console.error('Failed to update user progress in Supabase:', error);
        });
      } else if (reviewMode) {
        console.log('🔄 Review mode: skipping progress sync to database');
      }

      // Track element presentation in LLM Teacher
      if (current_element) {
        useLLMTeacherStore.getState().addElementMessage(current_element);
      }

      if (current_element?.element_type && InteractiveElementTypes.includes(current_element.element_type as ElementType)) {
        useChatStore.setState({ can_continue: false });
      }

      // Set generating to false immediately to show the new card without waiting for TTS
      setIsGenerating(false);

      // Play TTS in background without blocking UI
      if (current_element?.message_to_introduce && current_element.message_to_introduce.trim() !== '') {
        useAudioStore.setState({ playingId: current_element.id });
        useAudioStore.setState({ playingType: 'assistant' });
        useAudioStore.getState().playTTS(current_element?.message_to_introduce);
      }
    } else {

      if (canReview) {
        report(ReportEvent.LEARN_REVIEW_MODE_END, {
          quest_id: quest_id,
          chapter_id: chapter_id,
          lesson_id: learningUnitId,
        });
      } else {
        report(ReportEvent.USER_FINISH_LEARN, {
          quest_id: quest_id,
          chapter_id: chapter_id,
          lesson_id: learningUnitId,
        });
      }

      setIsGenerating(false);
      const current_lesson = useChatStore.getState().current_lesson;
      const quest = useChatStore.getState().quest;

      useMessageStore.getState().addMessage({
        role: 'assistant',
        type: 'text',
        content: "Congratulations! You have finished the lesson！",
      });
      fireConfettiRef.current?.();
      useChatStore.setState({ lesson_finished: true });

      // Only give rewards and update streak in non-review mode
      const reviewMode = useChatStore.getState().reviewMode;
      if (!reviewMode && user && !isPublicSkillLearn) {
        const tasks = await finishLesson(quest!, chapter_id, lesson_id, ModeType.LEARN);
        if (tasks.length > 0) {
          showTaskToastRef.current(tasks);
        }
        const completeToday = useStreakStore.getState().completeToday;
        if (await completeToday()) {
          //TODO: play animation.
        }

        // Create feed activity for learn completion
        createFeedActivity({
          feedType: 'learn_completed',
          contentId: lesson_id,
          metadata: {
            title: current_lesson?.title || 'Unknown Lesson',
            questTitle: quest?.title || '',
            coverImage: quest?.cover_image || '',
          }
        }, supabase).catch(error => {
          console.error('[Feed] Failed to create learn completion activity:', error);
        });
      } else if (reviewMode) {
        console.log('🔄 Review mode: skipping lesson rewards and streak update');
      }

      // sleep 1 second，然后自动弹出 congratulation popup（仅当未显示过时）
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (!hasShownCongratPopupRef.current) {
        setCongratPopup(true);
        hasShownCongratPopupRef.current = true;
      }
    }
  }, [canReview, chapter_id, isPublicSkillLearn, learningUnitId, lesson_id, messagesRef, quest_id, user]);

  const handleSubmit = useCallback(async (e: string) => {
    if (!e || e.trim().length === 0 || isGenerating || llmLoading) {
      console.log("ooooooooopus return ")
      return;
    }

    if (requestAuthPrompt('ask-pengi')) {
      return;
    }

    setShowInput(false);
    setIsGenerating(true);

    report(ReportEvent.LEARN_ASK_PENGI_TEXT, {
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: learningUnitId,
    });

    // 获取当前元素的所有相关信息
    const current_element = useChatStore.getState().current_element;
    const element_map = useChatStore.getState().element_map;
    const elementId = current_element?.id;
    const elementData = elementId ? element_map[elementId]?.data : null; // 用户答题数据
    const displayState = elementId ? useChatStore.getState().getElementDisplayState(elementId) : null; // 显示状态

    // 构建完整的元素上下文
    let contextElement: any = current_element;

    // 如果有显示状态或用户交互数据，创建增强的上下文
    if (displayState || elementData) {
      contextElement = {
        ...current_element,
        // 添加额外的上下文信息
        _context: {
          userInteraction: elementData || null,
          displayState: displayState || null
        }
      };
    }

    // Use LLM Teacher
    try {
      const response = await useLLMTeacherStore.getState().sendToLLM(e, contextElement);

      console.log("response in handleSubmit", response);
      setIsGenerating(false);

      if (response) {
        useMessageStore.getState().addMessage({
          role: 'assistant',
          type: 'text',
          content: response,
          isStreaming: true,
        });
        const contentLength = response.split(' ').length;
        await new Promise(resolve => setTimeout(resolve, contentLength * 70 + 500));
      }
    } catch (error) {
      console.error('Failed to get LLM response:', error);
      setIsGenerating(false);

      // Show fallback message on error
      useMessageStore.getState().addMessage({
        role: 'assistant',
        type: 'text',
        content: "Oops! Looks like I've slipped on some ice. Give me a moment to get back up, then we can chat!",
        isStreaming: true,
      });
    } finally {
      setTimeout(() => {
        if (!!chatMarkRef.current) {
          setShowInput(true);
        }
      }, 500);
    }
  }, [chapter_id, isGenerating, learningUnitId, lesson_id, llmLoading, quest_id, requestAuthPrompt, chatMarkRef]);

  const handleShowMoreDetails = useCallback(async () => {
    if (requestAuthPrompt('more-details')) {
      return;
    }

    report(ReportEvent.LEARN_SHOW_MORE_DETAILS, {
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: learningUnitId,
    });

    useAudioStore.getState().interruptAudio();
    // 检查是否是最后一条消息
    if (isGenerating) {
      return;
    }

    setLoadMore(true);

    useMessageStore.getState().addMessage({
      role: 'user',
      type: 'text',
      content: "Explain it in detail.",
    });

    const current_element = useChatStore.getState().current_element;
    if (current_element?.more_detail) {
      console.log("current_element?.more_detail", current_element?.more_detail);
      setIsGenerating(true);

      await new Promise(resolve => setTimeout(resolve, 1000));
      useMessageStore.getState().addMessage({
        role: 'assistant',
        type: 'text',
        content: current_element?.more_detail,
      });
      setIsGenerating(false);
    } else {
      // Ask LLM
      await handleSubmit("Explain it in detail.");
    }

    setLoadMore(false);
  }, [quest_id, chapter_id, learningUnitId, lesson_id, isGenerating, handleSubmit, requestAuthPrompt]);

  const hideAskPengiButton = useMemo(() => {
    return windowWidth <= 1024;
  }, [windowWidth]);

  const liveTutorStorageKey = useMemo(
    () => `${LEARN_LIVE_TUTOR_STORAGE_PREFIX}:${learningUnitId || 'default'}`,
    [learningUnitId],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !learningUnitId) return;

    try {
      const raw = window.localStorage.getItem(liveTutorStorageKey);
      if (!raw) {
        setLiveTutorCards([]);
        return;
      }

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        setLiveTutorCards([]);
        return;
      }

      const restoredCards = parsed
        .filter((card): card is LiveTutorCardItem => typeof card?.id === 'string' && typeof card?.sourceElementId === 'string')
        .map((card) => ({
          ...card,
          startSessionSignal: undefined,
          previewEntries: Array.isArray(card.previewEntries) ? card.previewEntries : [],
          previewShowBoardHeader: card.previewShowBoardHeader ?? true,
          previewStarStates: Array.isArray(card.previewStarStates) ? card.previewStarStates : ['pending', 'pending', 'pending'],
        }));

      setLiveTutorCards(restoredCards);
    } catch (error) {
      console.error('[Learn] Failed to restore live tutor cards:', error);
      setLiveTutorCards([]);
    }
  }, [learningUnitId, liveTutorStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || !learningUnitId) return;

    try {
      if (liveTutorCards.length === 0) {
        window.localStorage.removeItem(liveTutorStorageKey);
        return;
      }

      const serializableCards = liveTutorCards.map((card) => ({
        ...card,
        startSessionSignal: undefined,
      }));

      window.localStorage.setItem(liveTutorStorageKey, JSON.stringify(serializableCards));
    } catch (error) {
      console.error('[Learn] Failed to persist live tutor cards:', error);
    }
  }, [learningUnitId, liveTutorCards, liveTutorStorageKey]);

  const handleLiveTutorBoardStateChange = useCallback((
    cardId: string,
    state: {
      entries: BoardEntry[];
      showBoardHeader: boolean;
      starStates: Array<'pending' | 'success' | 'fail'>;
    },
  ) => {
    setLiveTutorCards((current) => current.map((card) => (
      card.id === cardId
        ? (
          JSON.stringify(card.previewEntries ?? []) === JSON.stringify(state.entries) &&
          (card.previewShowBoardHeader ?? true) === state.showBoardHeader &&
          JSON.stringify(card.previewStarStates ?? ['pending', 'pending', 'pending']) === JSON.stringify(state.starStates)
        )
          ? card
          : {
              ...card,
              previewEntries: state.entries,
              previewShowBoardHeader: state.showBoardHeader,
              previewStarStates: state.starStates,
            }
        : card
    )));
  }, []);

  const timelineItems = useMemo(() => {
    const messageItems = messages.map((message, index) => ({
      kind: 'message' as const,
      id: message.id || `learn_message_${index}`,
      sortKey: Date.parse(message.created_at || '') || index,
      message,
      index,
    }));

    const liveTutorItems = liveTutorCards.map((card, index) => ({
      kind: 'live-tutor' as const,
      id: card.id,
      sortKey: card.createdAt || (messages.length + index),
      card,
      index,
    }));

    return [...messageItems, ...liveTutorItems].sort((a, b) => {
      if (a.sortKey === b.sortKey) {
        return a.index - b.index;
      }
      return a.sortKey - b.sortKey;
    });
  }, [liveTutorCards, messages]);

  const handleShowExplain = useCallback(async (message: any) => {
    if (requestAuthPrompt('explain')) {
      return;
    }

    const elementId = message?.element?.id;
    if (elementId) {
      setQuizFollowupStateByElement((current) => ({
        ...current,
        [elementId]: {
          ...current[elementId],
          quickExplainUsed: true,
        },
      }));
    }

    report(ReportEvent.LEARN_SHOW_EXPLAIN, {
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: learningUnitId,
    });

    setLoadMore(true);
    useAudioStore.getState().interruptAudio();
    useMessageStore.getState().addMessage({
      role: 'user',
      type: 'text',
      content: "Explain it in detail.",
    });
    await new Promise(resolve => setTimeout(resolve, 1000));
    let explanation = message?.element?.questions?.[0]?.explanation;

    if (!explanation && message?.element?.question_id) {
      try {
        const question = await getQuestionById(message.element.question_id);
        explanation = question?.explanation;
      } catch (error) {
        console.error('Failed to load question explanation:', error);
      }
    }

    if (explanation) {
      useMessageStore.getState().addMessage({
        role: 'assistant',
        type: 'text',
        content: explanation,
      });
    } else {
      // Ask LLM
      await handleSubmit("Explain it in detail.");
    }
    setLoadMore(false);
  }, [quest_id, chapter_id, learningUnitId, lesson_id, handleSubmit, requestAuthPrompt]);

  const buildCurrentLearnLiveSelection = useCallback(async (): Promise<DemoSelection | null> => {
    const currentElement = useChatStore.getState().current_element;
    if (!currentElement) {
      return null;
    }

    const displayState = useChatStore.getState().getElementDisplayState(currentElement.id);
    const displayedOptions = Array.isArray(displayState?.shuffledOptions)
      ? displayState.shuffledOptions
          .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          .map((item: string) => item.trim())
      : undefined;

    const question = currentElement.questions?.[0]
      ?? (currentElement.question_id ? await getQuestionById(currentElement.question_id) : null);

    const problem = buildQuestionCardLiveTutorProblem({
      elementId: currentElement.id,
      elementType: currentElement.element_type,
      questionId: currentElement.question_id,
      question,
      subject: quest?.subject,
      displayedOptions: displayedOptions && displayedOptions.length > 0 ? displayedOptions : undefined,
    });

    if (problem) {
      return { kind: 'problem', problem };
    }

    const keyPropertySelection = buildKeyPropertyLiveTutorSelection(currentElement, quest?.subject);
    if (keyPropertySelection) {
      return keyPropertySelection;
    }

    const nonEmpty = (value: unknown): string | undefined =>
      typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

    const title = nonEmpty(currentElement.title)
      ?? nonEmpty(currentElement.question)
      ?? nonEmpty(currentElement?.questions?.[0]?.question)
      ?? 'Help me understand this lesson';

    const explanation = [
      nonEmpty(currentElement.content),
      nonEmpty(currentElement.description),
      nonEmpty(currentElement.more_detail),
      nonEmpty(currentElement?.questions?.[0]?.explanation),
    ].filter(Boolean).join('\n\n');

    return {
      kind: 'problem',
      problem: {
        id: `learn-context-${currentElement.id}`,
        question: title,
        explanation: explanation || title,
        subject: quest?.subject || undefined,
      },
    };
  }, [quest?.subject]);

  const buildAskPengiLiveContextSummary = useCallback(() => {
    const currentElement = useChatStore.getState().current_element;
    const elementMap = useChatStore.getState().element_map;
    const elementId = currentElement?.id;
    const elementData = elementId ? elementMap[elementId]?.data : null;
    const displayState = elementId ? useChatStore.getState().getElementDisplayState(elementId) : null;

    const summary = {
      currentElement: currentElement ? {
        type: currentElement.element_type,
        title: currentElement.title,
        question: currentElement.question,
        moreDetail: currentElement.more_detail,
      } : null,
      userInteraction: elementData || null,
      displayState: displayState || null,
    };

    return JSON.stringify(summary);
  }, []);

  const handleShowLiveTutor = useCallback(async () => {
    if (requestAuthPrompt('live')) {
      return;
    }

    const currentElement = useChatStore.getState().current_element;
    if (!currentElement) {
      return;
    }

    if (currentElement.element_type === ElementType.KEY_PROPERTY) {
      useChatStore.setState({ can_continue: true });
    }

    setQuizFollowupStateByElement((current) => ({
      ...current,
      [currentElement.id]: {
        ...current[currentElement.id],
        liveUsed: true,
      },
    }));

    const selection = await buildCurrentLearnLiveSelection();
    if (!selection) {
      return;
    }

    const nextCard: LiveTutorCardItem = {
      id: `live-tutor-${selection.kind}-${currentElement.id}`,
      sourceElementId: currentElement.id,
      problem: selection.kind === 'problem' ? selection.problem : undefined,
      selection,
      startSessionSignal: Date.now(),
      createdAt: Date.now(),
    };

    setLiveTutorCards((current) => {
      const existingIndex = current.findIndex((card) => card.sourceElementId === currentElement.id);
      if (existingIndex >= 0) {
        return current;
      }
      return [...current, nextCard];
    });
  }, [buildCurrentLearnLiveSelection, requestAuthPrompt]);

  const handleOpenAskPengiMessage = useCallback(() => {
    if (requestAuthPrompt('ask-pengi')) {
      return;
    }
    report(ReportEvent.LEARN_ASK_PENGI_BUTTON, {
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: learningUnitId,
      mode: 'message',
    });
    useAudioStore.getState().interruptAudio();
    setShowInput(true);
  }, [chapter_id, learningUnitId, quest_id, requestAuthPrompt]);

  const handleOpenAskPengiCall = useCallback(async () => {
    if (requestAuthPrompt('ask-pengi')) {
      return;
    }

    report(ReportEvent.LEARN_ASK_PENGI_BUTTON, {
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: learningUnitId,
      mode: 'call',
    });
    useAudioStore.getState().interruptAudio();
    setShowInput(false);

    const latestTimelineItem = timelineItems[timelineItems.length - 1];
    if (latestTimelineItem?.kind === 'live-tutor') {
      return;
    }

    const timestamp = Date.now();
    const sourceElementId = `ask-pengi-live-${learningUnitId}-${timestamp}`;
    const nextCard: LiveTutorCardItem = {
      id: sourceElementId,
      sourceElementId,
      askPengiCallLessonId: learningUnitId,
      selection: {
        kind: 'problem',
        problem: {
          id: sourceElementId,
          question: current_lesson?.title
            ? `Let's talk about ${current_lesson.title}.`
            : 'What would you like to discuss from this lesson?',
          explanation: current_element?.title || current_element?.more_detail || '',
          subject: quest?.subject || undefined,
        },
      },
      liveSessionOptions: {
        initialMessage: 'Hi! What would you like to discuss from this lesson today?',
      },
      contextOverrides: {
        contextType: 'learn_open_chat',
        question: current_lesson?.title || 'What would you like to discuss from this lesson?',
        subject: quest?.subject || undefined,
        questTitle: quest?.title || undefined,
        chapterTitle: current_chapter?.title || undefined,
        lessonTitle: current_lesson?.title || undefined,
        contextSummary: buildAskPengiLiveContextSummary(),
      },
      startSessionSignal: Date.now(),
      createdAt: timestamp,
    };

    setLiveTutorCards((current) => [...current, nextCard]);
  }, [buildAskPengiLiveContextSummary, chapter_id, current_element?.more_detail, current_element?.title, current_chapter?.title, current_lesson?.title, learningUnitId, quest?.subject, quest?.title, quest_id, requestAuthPrompt, timelineItems]);

  const openAskPengiMenu = useCallback(() => {
    if (askPengiMenuCloseTimeoutRef.current) {
      clearTimeout(askPengiMenuCloseTimeoutRef.current);
      askPengiMenuCloseTimeoutRef.current = null;
    }
    setIsAskPengiMenuOpen(true);
  }, []);

  const closeAskPengiMenu = useCallback(() => {
    if (askPengiMenuCloseTimeoutRef.current) {
      clearTimeout(askPengiMenuCloseTimeoutRef.current);
    }
    askPengiMenuCloseTimeoutRef.current = setTimeout(() => {
      setIsAskPengiMenuOpen(false);
      askPengiMenuCloseTimeoutRef.current = null;
    }, 120);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!askPengiMenuRef.current) return;
      if (!askPengiMenuRef.current.contains(event.target as Node)) {
        setIsAskPengiMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAskPengiMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (askPengiMenuCloseTimeoutRef.current) {
        clearTimeout(askPengiMenuCloseTimeoutRef.current);
      }
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleCompletedClick = () => {
    setCongratPopup(true);
    hasShownCongratPopupRef.current = true;
  }

  const scrollToBottom = () => {
    if (messagesRef.current) {
      // Use requestAnimationFrame to ensure DOM has been updated
      requestAnimationFrame(() => {
        const messagesBottom = messagesRef.current!.getBoundingClientRect().bottom + window.scrollY;
        // Subtract footer height (80px) to ensure content is visible above footer
        const targetScrollTop = messagesBottom - window.innerHeight + (hideAskPengiButton ? 80 : 130); // 100px buffer for footer
        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        });
      });
    }
  }

  useEffect(() => {
    // Auto-scroll to bottom using window scroll, accounting for fixed footer
    setTimeout(() => {
      if (messagesRef.current) {
        scrollToBottom();
        setTimeout(() => {
          chipHideRef.current = false; // 改用 ref，不触发渲染
        }, 1000);
      }
    }, 100);

    // Backup scroll to ensure it works
    setTimeout(() => {
      if (messagesRef.current) {
        scrollToBottom();
      }
    }, 500);

    // Additional scroll after layout is complete to ensure new cards are visible
    setTimeout(() => {
      if (messagesRef.current) {
        scrollToBottom();
      }
    }, 1000);

    // Use ResizeObserver to detect when card finishes rendering (especially for slow rendering cards)
    if (messagesRef.current) {
      let resizeTimeout: NodeJS.Timeout;
      const resizeObserver = new ResizeObserver(() => {
        // Debounce the scroll to avoid too many scrolls - only scroll after 200ms of no size changes
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          scrollToBottom();
        }, 200);
      });

      resizeObserver.observe(messagesRef.current);

      // Clean up observer after 2 seconds (card should be fully rendered by then)
      const cleanupTimer = setTimeout(() => {
        clearTimeout(resizeTimeout);
        resizeObserver.disconnect();
      }, 2000);

      return () => {
        clearTimeout(resizeTimeout);
        resizeObserver.disconnect();
        clearTimeout(cleanupTimer);
      };
    }
  }, [messages, showInput, can_continue, liveTutorCards]);

  // 添加学习时间埋点的逻辑
  useEffect(() => {
    // 确保有 quest_id 和用户 ID 才开始埋点
    if (!quest_id || !user?.id) {
      return;
    }

    // 创建埋点计时器
    const interval = setInterval(() => {
      // 获取当前 quest 的名称
      const questName = quest?.title || '';

      // 调用埋点函数，每次上报60秒（1分钟）
      UserTrackService.trackLearnDuration({
        user_id: user.id,
        quest_id: quest_id,
        quest_name: questName,
        learn_duration_seconds: 60 // 每分钟上报一次，增加60秒
      });
    }, 60000); // 每60秒（1分钟）执行一次

    trackingIntervalRef.current = interval; // 改用 ref，不触发渲染

    // 清理函数
    return () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
    };
  }, [quest_id, user?.id, quest?.title]);

  const isQuizCard = current_element?.element_type && QuizElementTypes.includes(current_element.element_type);

  // continue按钮在到底交互式卡片、并且没有完成的时候
  const continueButtonStuck = !can_continue && current_element?.element_type && InteractiveElementTypes.includes(current_element.element_type as ElementType);

  const handleContinue = useCallback(() => {
    if (continueButtonStuck) {
      console.log('should scroll to last element');

      // Scroll to the last element
      if (lastElementRef.current) {
        const elementTop = lastElementRef.current.getBoundingClientRect().top + window.scrollY;
        // Subtract header height to account for fixed header
        const headerHeight = betterExperienceTip ? 150 : 100;
        const targetScrollTop = elementTop - headerHeight - 20; // 20px additional offset

        window.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'smooth'
        });

        // 高亮显示最后一个元素，2秒后恢复
        setHighlightLastElement(true);
        setTimeout(() => {
          setHighlightLastElement(false);
        }, 2000);
      }

      setShowIncompleteTip(true);

      setTimeout(() => {
        setShowIncompleteTip(false);
      }, 3000);

      return;
    }

    report(ReportEvent.LEARN_CONTINUE, {
      quest_id: quest_id,
      chapter_id: chapter_id,
      lesson_id: learningUnitId,
    });

    chipHideRef.current = true; // 改用 ref，不触发渲染
    useAudioStore.getState().interruptAudio();
    setShowInput(false);
    chatMarkRef.current = false;

    const current_element = useChatStore.getState().current_element;
    handleElementFinish(current_element?.id!, 'user_proceed');
  }, [continueButtonStuck, betterExperienceTip, quest_id, chapter_id, learningUnitId, lastElementRef, chatMarkRef, handleElementFinish]);

  const showVolume = useCallback((message: any) => {
    let show = false;
    if (isPlaying) {
      if (message.type === 'text' && message.id === playingId && playingType === 'assistant') {
        show = true;
      }
      if (message.element && playingId === message.element.id && playingType === 'assistant') {
        show = true;
      }
    }
    return show;
  }, [playingId, isPlaying, playingType]);

  useEffect(() => {
    const isFirstLearnGuide2 = localStorage.getItem(STORAGE_KEYS.LEARN_TUTORIAL);
    if (headerRef.current && !isFirstLearnGuide2) {
      // 确保元素真正渲染并可见
      const element = headerRef.current;
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && stepRefs[0] === null) {
        setStepRefs(prev => {
          const newStepRefs = [...prev];
          newStepRefs[0] = headerRef as unknown as RefObject<HTMLDivElement>;
          return newStepRefs;
        });
      }
    }
  }, [headerRef.current, learnTutorial]); // 移除stepRefs依赖避免循环

  useEffect(() => {
    const isFirstLearnGuide2 = localStorage.getItem(STORAGE_KEYS.LEARN_TUTORIAL);
    if (pengiButtonRef.current && !isFirstLearnGuide2) {
      // 确保元素真正渲染并可见
      const element = pengiButtonRef.current;
      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && stepRefs[1] === null) {
        setStepRefs(prev => {
          const newStepRefs = [...prev];
          newStepRefs[1] = pengiButtonRef as unknown as RefObject<HTMLDivElement>;
          return newStepRefs;
        });
      }
    }
  }, [pengiButtonRef.current, learnTutorial]); // 移除stepRefs依赖避免循环

  const renderTutorial = () => {
    // 根据用户的role和登录的device来决定是否显示tutorial
    if (learnTutorial) {
      if (device !== 'mobile') {
        return (
          <Suspense fallback={null}>
            <LearnGuideOverlay 
              visible={learnTutorial} 
              stepRefs={stepRefs}
              onFinish={finishLearnTutorial}
            />
          </Suspense>
        )
      } else {
        return (
          <Suspense fallback={null}>
            <LearnChildMobileGuide 
              onFinish={finishLearnTutorial}
            />
          </Suspense>
        )
      }
    }

    return null;
  }

  const renderMoreDetails = () => {
    const lastMessage = messages[messages.length - 1];
    const currentElementMessage = (
      lastMessage?.type === 'element' &&
      current_element?.id &&
      lastMessage.element?.id === current_element.id
    ) ? lastMessage : null;

    // 如果最后一条消息不是 element 类型，或者在特殊隐藏位置，不渲染整个区域
    if (lastMessage?.type !== 'element' || (HideMoreDetailPosition.includes(current_element?.element_type as ElementType))) {
      return null;
    }

    // 判断是否应该隐藏按钮（但保留容器空间）
    const shouldHideButton = isQuizCard && !can_continue;
    const currentFollowupState = current_element?.id ? quizFollowupStateByElement[current_element.id] : undefined;
    const hasUsedQuickExplain = Boolean(currentFollowupState?.quickExplainUsed);
    const hasUsedLiveTutor = Boolean(currentFollowupState?.liveUsed);
    const shouldShowKeyPropertyLive = (
      current_element?.element_type === ElementType.KEY_PROPERTY &&
      isMathLearnLiveTutorEnabled(mode, quest?.subject) &&
      !hasUsedLiveTutor
    );
    const hasCurrentElementLiveCard = current_element?.id
      ? liveTutorCards.some((card) => card.sourceElementId === current_element.id)
      : false;
    const latestTimelineItem = timelineItems[timelineItems.length - 1];
    const hasAskPengiLiveCard = (
      latestTimelineItem?.kind === 'live-tutor' &&
      latestTimelineItem.card.askPengiCallLessonId === learningUnitId
    );

    if (hasAskPengiLiveCard || hasCurrentElementLiveCard) {
      return null;
    }

    return (
      <div className="md:w-3/4 w-full flex flex-row justify-end items-center mb-5 h-[56px] md:h-[60px]">
        <div className="w-[200px] gap-4">
          {
            !shouldHideButton && (
              isQuizCard ? (
                hasUsedQuickExplain && hasUsedLiveTutor ? null : hasUsedQuickExplain ? (
                  <div className="flex justify-end">
                    <LiveButton
                      disabled={loadMore}
                      label="Live"
                      onClick={handleShowLiveTutor}
                    />
                  </div>
                ) : hasUsedLiveTutor ? (
                  <button
                    disabled={loadMore}
                    onClick={async () => handleShowExplain(currentElementMessage)}
                    className="w-full md:py-3 py-2 rounded-full flex flex-row items-center justify-center border text-xl emphasize-text"
                    style={{ color: '#42C52D', borderColor: '#42C52D' }}
                  >
                    Explain
                  </button>
                ) : (
                  <div className="flex justify-end">
                    <LiveExplainButton
                      disabled={loadMore}
                      liveLabel="Explain"
                      onLiveTutor={handleShowLiveTutor}
                      onQuickExplain={() => handleShowExplain(currentElementMessage)}
                    />
                  </div>
                )
              ) : shouldShowKeyPropertyLive ? (
                <div className="flex justify-end">
                  <LiveButton
                    disabled={loadMore}
                    label="Live"
                    variant="outline"
                    onClick={handleShowLiveTutor}
                  />
                </div>
              ) : (current_element?.element_type === ElementType.FLASH_CARD || current_element?.element_type === ElementType.SPELLING_DICTATION) ? (
                <button
                  disabled
                  className="emphasize-text w-full md:py-3 py-2 rounded-full text-white flex flex-row items-center justify-center text-xl border opacity-30"
                  style={{ borderColor: '#42C52D', color: '#42C52D' }}
                >
                  More details
                </button>
              ) : (!lesson_finished ? (
                <button
                  disabled={loadMore}
                  onClick={async () => handleShowMoreDetails()}
                  className="w-full md:py-3 py-2 rounded-full flex flex-row items-center justify-center border text-xl emphasize-text"
                  style={{ color: '#42C52D', borderColor: '#42C52D' }}
                >
                  More details
                </button>
              ) : (
                <button
                  disabled
                  className="emphasize-text w-full md:py-3 py-2 rounded-full text-white flex flex-row items-center justify-center text-xl border"
                  style={{ borderColor: '#42C52D', color: '#42C52D' }}
                >
                  More details
                </button>
              )
              )
            )
          }
        </div>
      </div>
    )
  }

  const renderInput = useCallback(() => {
    return (
      <div className="w-full flex flex-row justify-center items-center">
        <div className="w-full md:w-3/4 flex flex-row justify-center items-center">
          <LearnInput
            handleSubmit={(input) => {
              if (input.trim().length === 0) {
                return;
              }
              chatMarkRef.current = true;
              handleSubmit(input);
              setShowInput(false);
              useMessageStore.getState().addMessage({
                role: 'user',
                type: 'text',
                content: input.trim(),
              });
            }}
            onClose={() => {
              chatMarkRef.current = false;
              setShowInput(false);
            }}
          />
        </div>
      </div>
    )
  }, [handleSubmit, chatMarkRef]);

  if (loading || (isPublicStudyExperience && !authInitialized)) {
    if (isPublicStudyExperience) {
      return (
        <div className="w-full min-h-screen flex justify-center overflow-x-hidden" style={{ backgroundColor: colors.homeBgColor }}>
          <div className="fixed top-0 left-0 right-0 z-50 flex justify-center border-b border-[#E6D9C8] bg-white/95">
            <div className="flex h-[92px] w-full items-center justify-between px-6 md:px-10">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-[12px] bg-[#EFE4D6] animate-pulse" />
                <div className="h-10 w-10 rounded-[12px] bg-[#EFE4D6] animate-pulse" />
              </div>

              <div className="flex min-w-0 flex-1 items-center justify-center gap-4 px-6">
                <div className="h-14 w-10 rounded-[10px] bg-[#EADBC8] animate-pulse" />
                <div className="flex min-w-0 max-w-[720px] flex-1 flex-col items-center gap-2">
                  <div className="h-4 w-48 rounded-full bg-[#EADBC8] animate-pulse" />
                  <div className="h-7 w-[min(680px,72vw)] rounded-full bg-[#F1E5D7] animate-pulse" />
                  <div className="h-4 w-[min(420px,42vw)] rounded-full bg-[#F6EEE4] animate-pulse" />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="h-10 w-24 rounded-full bg-[#F3E7D5] animate-pulse" />
              </div>
            </div>
          </div>
          <div className="w-full pt-[92px]" />
        </div>
      );
    }

    return (
      <div className="w-full h-screen flex justify-center items-center">
        <div className="emphasize-text text-3xl" style={{ color: colors.loadingColor }}>Loading...</div>
      </div>
    );
  }

  return (
    <PublicStudyAuthPromptProvider requestAuthPrompt={requestAuthPrompt}>
    <SubjectManagementModal
      visible={!!subjectPaywallSubject}
      onClose={() => { setSubjectPaywallSubject(null); router.push('/quest'); }}
    />
    <ConfirmSubjectSelectionModal
      visible={!!confirmSubjectBeforeStart}
      subject={confirmSubjectBeforeStart}
      currentSubject={profile?.free_subject ?? null}
      onClose={() => {
        setConfirmSubjectBeforeStart(null);
        router.push('/quest');
      }}
      onViewDetails={() => {
        setSubjectPaywallSubject(confirmSubjectBeforeStart);
        setConfirmSubjectBeforeStart(null);
      }}
      onConfirmed={async () => {
        setConfirmSubjectBeforeStart(null);
        router.refresh();
      }}
    />
    <div className="w-full min-h-screen flex justify-center overflow-x-hidden" style={{ backgroundColor: colors.homeBgColor }}>
      <div className="flex flex-col w-full min-h-screen inset-y-0 items-center max-w-[100vw]">
        {/* Fixed Header */}
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center" style={{ backgroundColor: colors.mainBgColor }}>
          <Header
            questId={quest_id}
            titleRef={headerRef}
            showAllChapters={showAllChapters}
            setShowAllChapters={setShowAllChapters}
            currentProgress={currentProgress}
            totalDiamondImageRef={totalDiamondImageRef}
            cover_image={quest?.cover_image!}
            chapter_title={isPublicSkillLearn ? '' : (current_chapter?.title || '')}
            lesson_title={isPublicSkillLearn ? (publicSkillTitle || current_lesson?.title || '') : (current_lesson?.title || '')}
            animatedBar={true}
            from={from}
            modeType={mode}
            hideTableOfContents={isPublicSkillLearn}
            hideChapterTitle={isPublicSkillLearn}
            hideCoverImage={isPublicSkillLearn}
            previewOnly={Boolean(isGuestMode || isParentPreviewMode)}
            initialChapterId={chapter_id}
            initialLessonId={lesson_id}
            onAuthRequired={isGuestMode || isParentPreviewMode ? () => requestAuthPrompt('complete-question') : undefined}
          />
        </div>

        {/* Redo Mode Indicator */}
        {reviewMode && (
          <div className="fixed left-0 right-0 z-40 flex justify-center transition-all" style={{ top: betterExperienceTip ? '160px' : '110px' }}>
            <div className="bg-gray-500 text-white px-6 py-2 rounded-full flex items-center gap-3 animate-fade-in border-2 border-white shadow-lg">
              <span className="text-lg emphasize-text">Learn Review Mode</span>
            </div>
          </div>
        )}

        {/* Scrollable Content Area with proper spacing */}
        <div className="w-full relative" style={{ paddingTop: reviewMode ? (betterExperienceTip ? '200px' : '150px') : (betterExperienceTip ? `${baseHeight + 30}px` : `${baseHeight}px`), paddingBottom: hideAskPengiButton ? '80px' : '130px' }}>
          <div className="learn-main-container w-full mx-auto px-4" ref={messagesRef}>
            <div className="flex flex-col items-center gap-2 pt-4">
              {timelineItems.map((item) => {
                if (item.kind === 'live-tutor') {
                  return (
                    <div
                      key={item.id}
                      className="flex flex-col w-full items-center relative"
                    >
                      <div className="flex flex-col bg-white w-full md:w-3/4 rounded-xl p-4 transition-all duration-300 border border-gray-300">
                        <div className="flex flex-row justify-between">
                          <div className="flex flex-row items-center gap-2">
                            <AssistantAvatar />
                            <span
                              className="text-lg emphasize-text"
                              style={{ color: colors.mainTitleColor }}
                            >
                              Live Tutor
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 px-5">
                          <LiveTutorCard
                            problem={item.card.problem}
                            selection={item.card.selection}
                            liveSessionOptions={item.card.liveSessionOptions}
                            contextOverrides={item.card.contextOverrides}
                            startSessionSignal={item.card.startSessionSignal}
                            previewEntries={item.card.previewEntries}
                            previewShowBoardHeader={item.card.previewShowBoardHeader}
                            previewStarStates={item.card.previewStarStates}
                            onBoardStateChange={(state) => handleLiveTutorBoardStateChange(item.card.id, state)}
                            onRequireAuth={() => requestAuthPrompt('live')}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }

                const { message, index } = item;
                // Find the last element type message index
                const lastElementIndex = messages.map((m, i) => m.type === 'element' ? i : -1).filter(i => i !== -1).pop();
                const isLastElement = message.type === 'element' && index === lastElementIndex;

                return (
                <div
                  key={'learn_message_' + index}
                  className="flex flex-col w-full items-center relative"
                  ref={isLastElement ? lastElementRef : null}
                >
                  <div className={`flex flex-col bg-white w-full md:w-3/4 rounded-xl p-4 transition-all duration-300 ${
                    isLastElement && highlightLastElement
                      ? 'border-3 border-[#42C52D]'
                      : 'border border-gray-300'
                  }`}>
                    <div className="flex flex-row justify-between">
                      <div className="flex flex-row items-center gap-2">
                        {message.role === 'user' ? <LetterAvatar backgroundColor={'#805ad5'} /> : <AssistantAvatar />}
                        <span
                          className="text-lg emphasize-text"
                          style={{ color: colors.mainTitleColor }}>
                          {message.role === 'assistant' ? quest?.instructor_profile.name : 'You'}
                        </span>
                        {
                          showVolume(message) && (
                            <Volume />
                          )
                        }
                      </div>
                      {
                        message.type === 'element' && message.element && (
                          <CardInfo
                            element={message.element}
                            totalDiamondImageRef={totalDiamondImageRef}
                          />
                        )
                      }
                    </div>
                    {
                      message.type === 'element' && message.element ? (
                        <div className="mt-2 px-5">
                          <LearnCard
                            message={message}
                            handleElementFinish={handleElementFinish}
                          />
                        </div>
                      ) : (index === messages.length - 1 && !!message.isStreaming) ?
                        (
                          <div key={index} className="rounded-lg p-2 px-8" style={{ fontSize: 14, fontWeight: 500, color: colors.mainTextColor }}>
                            <TypewriterEffect
                              speed={50}
                              content={message?.content || ''}
                              onContentChange={scrollToBottom}
                              autoScrollThreshold={50}
                            />
                          </div>
                        )
                       : (
                        <div key={index} className="rounded-lg p-2 px-8" style={{ fontSize: 14, fontWeight: 500, color: colors.mainTextColor }}>
                          <MarkdownComponent content={message?.content || ''} />
                        </div>
                       )
                    }
                  </div>
                </div>
                );
              })}
              <div className="w-full flex flex-row justify-center items-center">
                {(isGenerating) && (
                  <div className="flex flex-row items-center gap-2 bg-white w-full md:w-3/4 border border-gray-300 rounded-xl p-4 animate-fade-in">
                    <AssistantAvatar />
                    <span className="text-lg emphasize-text animate-pulse" style={{ color: colors.mainTextColor }}>
                      Thinking...
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="w-full flex flex-row justify-center items-center">
              {renderMoreDetails()}
            </div>
            {
              showInput && renderInput()
            }
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="fixed bottom-0 left-0 right-0 z-40 flex justify-center" style={{ backgroundColor: colors.homeBgColor }}>
          <div className="learn-main-container flex flex-col justify-center items-center">
            <div className="w-full sm:w-3/4 items-center justify-center flex flex-row gap-3 sm:gap-4 px-3 sm:px-4" style={{ height: '80px' }}>
              <div
                ref={askPengiMenuRef}
                className="relative w-[120px] sm:w-[160px] lg:w-[180px] flex-shrink-0 flex flex-col items-center justify-center"
                onMouseEnter={openAskPengiMenu}
                onMouseLeave={closeAskPengiMenu}
              >
                <div
                  aria-hidden={!isAskPengiMenuOpen}
                  className={`absolute bottom-full left-1/2 z-30 mb-3 -translate-x-1/2 transition-all duration-250 ease-out ${
                    isAskPengiMenuOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
                  }`}
                >
                  <div className="flex min-w-[184px] flex-col gap-2 rounded-[24px] border border-[#DDE8D7] bg-white/96 p-2 shadow-[0_16px_30px_rgba(76,99,55,0.14)] backdrop-blur-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAskPengiMenuOpen(false);
                        void handleOpenAskPengiCall();
                      }}
                      className="display-text-bold inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#42C52D] px-5 py-2.5 text-base text-white transition hover:brightness-105"
                    >
                      <Phone className="h-4 w-4" />
                      Call Pengi
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAskPengiMenuOpen(false);
                        handleOpenAskPengiMessage();
                      }}
                      className="display-text-bold inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[#D9E4D0] bg-white px-5 py-2.5 text-base text-[#3A4A36] transition-colors hover:bg-[#F6FAF2]"
                    >
                      <MessageCircleMore className="h-4 w-4" />
                      Send Message
                    </button>
                  </div>
                </div>
                <div className="w-full h-[42px] lg:h-[48px]" ref={pengiButtonRef}>
                  <span className="absolute right-[2px] top-[-9px] z-20 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-[#FFF4DA] text-[#42C52D] shadow-[0_6px_12px_rgba(125,193,70,0.2)]">
                    <Phone className="h-3.5 w-3.5" />
                  </span>
                  <NormalButton
                    colorType="green"
                    onClick={() => setIsAskPengiMenuOpen((current) => !current)}
                    className="mt-[-2px]"
                    textClassName="py-[9px] text-base sm:text-xl md:text-2xl"
                  >
                    Ask Pengi
                  </NormalButton>
                </div>
                <button
                  onClick={() => {
                    setIsAskPengiMenuOpen((current) => !current);
                  }}
                  className="hidden lg:block absolute -top-[60px] left-[10px] lg:-top-[110px] lg:left-[30px]"
                >
                  <Image
                    alt="ask_pengi_1"
                    src={'/assets/ask_pengi_1.png'}
                    className="w-[65px] lg:w-[120px] h-[80px] lg:h-[140px]"
                    width={89}
                    height={105}
                    style={{
                      pointerEvents: 'none',
                    }}
                    optimizedWidth={200}
                  />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                {!lesson_finished ? (
                  <div className="relative group inline-block w-full">
                    <NormalButton
                      colorType={continueButtonStuck ? 'gray' : 'green'}
                      disabled={isGenerating}
                      onClick={handleContinue}
                      textClassName="py-[9px] text-base sm:text-xl md:text-2xl"
                    >
                      Continue
                    </NormalButton>
                    {
                      (showIncompleteTip && getDisabledContinueButtonTips(current_element).length > 0) && (
                        <div className="emphasize-text absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-2 text-sm text-white bg-[#3C3C3C] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                          {getDisabledContinueButtonTips(current_element)}
                        </div>
                      )
                    }
                  </div>
                ) : !canReview ? (
                  <NormalButton
                    onClick={handleCompletedClick}
                    textClassName="py-[9px] text-base sm:text-xl md:text-2xl"
                  >
                    Completed
                  </NormalButton>
                ) : (
                  (
                    <NormalButton
                      onClick={() => {
                        report(ReportEvent.LEARN_REVIEW_MODAL_OPEN, {
                          quest_id: quest_id,
                          chapter_id: chapter_id,
                          lesson_id: learningUnitId,
                        });
                        setShowReviewModal(true);
                      }}
                      textClassName="py-[9px] text-base sm:text-xl md:text-2xl"
                    >
                      Redo This Lesson
                    </NormalButton>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderTutorial()}
      <Suspense fallback={null}>
        <CongratPopup
          visible={congratPopup}
          type="learn"
          lessonMessage={<span>{current_lesson?.title || ''}</span>}
          onClose={() => setCongratPopup(false)}
          onNextClicked={() => {
            setCongratPopup(false);
            if (from === 'parent-assignment') {
              router.push('/');
              return;
            }
            setShowAllChapters(true);
          }}
        />
      </Suspense>
      <Suspense fallback={null}>
        <ReviewModal
          visible={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          onNextClicked={() => {
            report(ReportEvent.LEARN_REVIEW_MODE_START, {
              quest_id: quest_id,
              chapter_id: chapter_id,
              lesson_id: learningUnitId,
            });
            useChatStore.getState().enterReviewMode();
            setShowReviewModal(false);
          }}
        />
      </Suspense>
      <Suspense fallback={null}>
        <CustomFeedbackSurvey />
      </Suspense>
      <PublicStudyAuthPromptModal
        visible={showAuthPrompt}
        reason={authPromptReason}
        onClose={() => setShowAuthPrompt(false)}
      />
    </div>
    </PublicStudyAuthPromptProvider>
  );
}
